import { Activity, BarChart3 } from "lucide-react";
import { useMemo, useState } from "react";

import type { ApiError } from "@/api/client";
import {
  type UsageQueryParams,
  useCurrentUser,
  useOrganizations,
  useUsageEvents,
  useUsageSummary,
} from "@/api/hooks";
import type { UsageRollupRow } from "@/api/types";
import { formatCents, formatTime, shortId } from "@/lib/format";

/** AI usage / billing dashboard.
 *
 * Two stacked views:
 *
 * 1. **Rollup** — one row per ``(org, year_month, operation)`` triple,
 *    grouped to a per-month-per-org "what we'd invoice."
 * 2. **Events** — paginated raw cost events for spot-checking.
 *
 * The numbers are penny-accurate by construction: cost_micros is summed
 * server-side (no per-call rounding), and only the visible cents column
 * is ceil'd at row + grand-total boundaries. Sum cells across rows by
 * eye, not by math — they don't add up to the grand total exactly
 * (which is also the right behavior, since the grand total is the
 * one that matters for billing).
 */
export function UsagePage() {
  const me = useCurrentUser();
  const isAdmin = me.data?.role === "pencilout_admin";

  // Default window: this month. Roll back via the date inputs.
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  const [start, setStart] = useState<string>(toISODate(monthStart));
  const [end, setEnd] = useState<string>(toISODate(today));
  const [orgId, setOrgId] = useState<string>("");
  const [operation, setOperation] = useState<string>("");

  const orgsList = useOrganizations();
  const orgs = orgsList.data ?? [];

  const summaryParams: UsageQueryParams = useMemo(
    () => ({
      start,
      end,
      ...(orgId ? { organization_id: orgId } : {}),
    }),
    [start, end, orgId],
  );

  const eventsParams: UsageQueryParams = useMemo(
    () => ({
      start,
      end,
      ...(orgId ? { organization_id: orgId } : {}),
      ...(operation ? { operation } : {}),
      limit: 50,
    }),
    [start, end, orgId, operation],
  );

  const summary = useUsageSummary(summaryParams);
  const events = useUsageEvents(eventsParams);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-mono text-2xl font-bold tracking-tight">AI Usage</h1>
        <p className="mt-1 max-w-3xl text-sm text-graphite-soft">
          {isAdmin
            ? "Per-org pass-through cost for every Gemini call across the platform. Numbers are penny-accurate — micros are summed server-side, cents are ceil'd once at the boundary."
            : "What your organization spent on AI processing. Pass-through cost — what we paid Gemini for your walkthroughs and quotes."}
        </p>
      </div>

      {/* ---- Filters ---- */}
      <div className="rounded-lg border border-rule bg-paper-warm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <Field label="From">
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="rounded border border-rule bg-paper px-2 py-1 font-mono text-sm"
            />
          </Field>
          <Field label="To">
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="rounded border border-rule bg-paper px-2 py-1 font-mono text-sm"
            />
          </Field>
          {isAdmin && (
            <Field label="Organization">
              <select
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                className="rounded border border-rule bg-paper px-2 py-1 text-sm"
              >
                <option value="">All organizations</option>
                {orgs.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Operation">
            <select
              value={operation}
              onChange={(e) => setOperation(e.target.value)}
              className="rounded border border-rule bg-paper px-2 py-1 text-sm"
            >
              <option value="">All operations</option>
              <option value="walkthrough_transcribe">Walkthrough transcription</option>
              <option value="quote_generate">Quote generation</option>
              <option value="quote_suggest">Quote suggestions</option>
            </select>
          </Field>
        </div>
      </div>

      {/* ---- Grand total ---- */}
      <div className="flex items-start justify-between gap-6 rounded-lg border border-rule bg-pencil-yellow-soft p-4">
        <div className="flex flex-wrap gap-8">
          <Stat
            label={isAdmin ? "Billed (with markup)" : `Total (${start} → ${end})`}
            value={summary.data ? formatCents(summary.data.grand_total_billed_cents) : "—"}
            sub={
              summary.data && summary.data.grand_total_billed_micros > 0
                ? `${summary.data.grand_total_billed_micros.toLocaleString()} µ¢`
                : undefined
            }
          />
          {isAdmin && summary.data?.grand_total_raw_cents !== null && (
            <>
              <Stat
                label="Raw (Gemini cost)"
                value={summary.data ? formatCents(summary.data.grand_total_raw_cents ?? 0) : "—"}
                muted
              />
              <Stat
                label="Markup profit"
                value={
                  summary.data
                    ? formatCents(
                        summary.data.grand_total_billed_cents -
                          (summary.data.grand_total_raw_cents ?? 0),
                      )
                    : "—"
                }
                accent
              />
            </>
          )}
        </div>
        <BarChart3 className="h-12 w-12 flex-none text-pencil-yellow" aria-hidden />
      </div>

      {/* ---- Rollup table ---- */}
      <RollupTable
        rows={summary.data?.rows ?? []}
        loading={summary.isLoading && !summary.data}
        error={summary.error}
        showOrg={isAdmin}
        isAdmin={isAdmin}
      />

      {/* ---- Recent events ---- */}
      <EventsTable
        params={eventsParams}
        loading={events.isLoading && !events.data}
        error={events.error}
        events={events.data?.events ?? []}
        total={events.data?.total ?? 0}
        showOrg={isAdmin}
        isAdmin={isAdmin}
      />
    </div>
  );
}

// ----------------------------------------------------------------

function RollupTable(props: {
  rows: UsageRollupRow[];
  loading: boolean;
  error: ApiError | null;
  showOrg: boolean;
  isAdmin: boolean;
}) {
  if (props.loading) {
    return <p className="text-sm text-graphite-soft">Loading rollup…</p>;
  }
  if (props.error) {
    return (
      <p className="text-sm text-red-pencil">{props.error.detail || "Could not load usage."}</p>
    );
  }
  if (props.rows.length === 0) {
    return (
      <div className="rounded-lg border border-rule bg-paper-warm p-8 text-center">
        <Activity className="mx-auto h-6 w-6 text-graphite-faint" aria-hidden />
        <p className="mt-3 text-sm text-graphite-soft">No AI calls in this window.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-rule">
      <table className="w-full text-sm">
        <thead className="bg-paper-warm text-xs uppercase tracking-wider text-graphite-soft">
          <tr>
            <Th>Month</Th>
            {props.showOrg && <Th>Organization</Th>}
            <Th>Operation</Th>
            <Th align="right">Calls</Th>
            <Th align="right">Input tok</Th>
            <Th align="right">Output tok</Th>
            {props.isAdmin && <Th align="right">Raw</Th>}
            {props.isAdmin && <Th align="right">Markup</Th>}
            <Th align="right">{props.isAdmin ? "Billed" : "Cost"}</Th>
          </tr>
        </thead>
        <tbody>
          {props.rows.map((row) => (
            <tr
              key={`${row.organization_id}-${row.year_month}-${row.operation}`}
              className="border-t border-rule"
            >
              <Td mono>{row.year_month}</Td>
              {props.showOrg && <Td>{row.organization_name}</Td>}
              <Td mono>{row.operation}</Td>
              <Td align="right">{row.call_count.toLocaleString()}</Td>
              <Td align="right">{row.input_tokens.toLocaleString()}</Td>
              <Td align="right">{row.output_tokens.toLocaleString()}</Td>
              {props.isAdmin && (
                <Td align="right" mono>
                  <span className="text-graphite-soft">
                    {row.raw_cost_cents !== null ? formatCents(row.raw_cost_cents) : "—"}
                  </span>
                </Td>
              )}
              {props.isAdmin && (
                <Td align="right" mono>
                  <span className="text-graphite-soft">
                    {row.markup_pct !== null && row.markup_pct > 0
                      ? `${(row.markup_pct * 100).toFixed(1)}%`
                      : "—"}
                  </span>
                </Td>
              )}
              <Td align="right" mono>
                <strong>{formatCents(row.billed_cents)}</strong>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventsTable(props: {
  params: UsageQueryParams;
  loading: boolean;
  error: ApiError | null;
  events: import("@/api/types").UsageEventResponse[];
  total: number;
  showOrg: boolean;
  isAdmin: boolean;
}) {
  return (
    <div className="space-y-3">
      <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
        Recent events ({props.total})
      </h2>
      {props.loading && <p className="text-sm text-graphite-soft">Loading events…</p>}
      {props.error && (
        <p className="text-sm text-red-pencil">{props.error.detail || "Could not load events."}</p>
      )}
      {!props.loading && !props.error && props.events.length === 0 && (
        <p className="text-sm text-graphite-soft">No events in this window.</p>
      )}
      {props.events.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-rule">
          <table className="w-full text-xs">
            <thead className="bg-paper-warm text-xs uppercase tracking-wider text-graphite-soft">
              <tr>
                <Th>When</Th>
                {props.showOrg && <Th>Org</Th>}
                <Th>Operation</Th>
                <Th>Model</Th>
                <Th align="right">Tokens (in/out)</Th>
                {props.isAdmin && <Th align="right">Raw</Th>}
                <Th align="right">{props.isAdmin ? "Billed" : "Cost"}</Th>
                <Th align="right">Duration</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {props.events.map((ev) => (
                <tr key={ev.id} className="border-t border-rule">
                  <Td mono>{formatTime(ev.created_at)}</Td>
                  {props.showOrg && <Td>{ev.organization_name}</Td>}
                  <Td mono>{ev.operation}</Td>
                  <Td mono>{ev.model}</Td>
                  <Td align="right" mono>
                    {ev.input_tokens.toLocaleString()} / {ev.output_tokens.toLocaleString()}
                  </Td>
                  {props.isAdmin && (
                    <Td align="right" mono>
                      <span className="text-graphite-soft">
                        {ev.raw_cost_cents !== null ? formatCents(ev.raw_cost_cents) : "—"}
                      </span>
                    </Td>
                  )}
                  <Td align="right" mono>
                    {formatCents(ev.billed_cents)}
                  </Td>
                  <Td align="right" mono>
                    {ev.duration_ms !== null ? `${ev.duration_ms}ms` : "—"}
                  </Td>
                  <Td>
                    {ev.succeeded ? (
                      <span className="text-graphite-soft">ok</span>
                    ) : (
                      <span className="text-red-pencil" title={ev.error_message ?? ""}>
                        partial
                      </span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat(props: {
  label: string;
  value: string;
  sub?: string;
  muted?: boolean;
  accent?: boolean;
}) {
  return (
    <div>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-graphite-soft">
        {props.label}
      </h2>
      <p
        className={`mt-1 font-mono text-3xl font-bold tabular-nums ${
          props.accent ? "text-graphite" : props.muted ? "text-graphite-soft" : "text-graphite"
        }`}
      >
        {props.value}
      </p>
      {props.sub && (
        <p className="mt-1 font-mono text-xs text-graphite-soft tabular-nums">({props.sub})</p>
      )}
    </div>
  );
}

// ----------------------------------------------------------------

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: caller passes the input as `children`; biome can't see through to confirm it, but the input renders as a descendant of <label> at runtime.
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wider text-graphite-soft">
        {label}
      </span>
      {children}
    </label>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th className={`px-3 py-2 ${align === "right" ? "text-right" : "text-left"}`}>{children}</th>
  );
}

function Td({
  children,
  align = "left",
  mono,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  mono?: boolean;
}) {
  return (
    <td
      className={`px-3 py-2 ${align === "right" ? "text-right" : ""} ${
        mono ? "font-mono tabular-nums" : ""
      }`}
    >
      {children}
    </td>
  );
}

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// Avoid an unused-import warning when shortId isn't used directly here
// (kept for future drill-in patterns).
void shortId;
